import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { parse } from 'mathjs';
import { MarkdownModule } from 'ngx-markdown';

@Component({
  selector: 'app-math-equation-dialog',
  imports: [TranslateModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, FormsModule, MarkdownModule],
  template: `
    <div class="standard-dialog">
      <div class="header" mat-dialog-title>
        <div class="title-section">
          <h2>{{ 'CreateChunk.MathExpression' | translate }}</h2>
          <p class="subtitle">{{ 'CreateChunk.MathExpressionSubtitle' | translate }}</p>
        </div>
        <button mat-icon-button (click)="dialogRef.close()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="form-content pt-4">
        <div class="w-100 mb-3">
          <mat-label class="form-label mb-1">{{ 'CreateChunk.ExpressionLabel' | translate }}</mat-label>
          <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-100">
            <input matInput [(ngModel)]="expression" (keydown.enter)="submit()" [placeholder]="'CreateChunk.ExpressionPlaceholder' | translate" autofocus />
          </mat-form-field>
        </div>
        @if (previewLatex) {
          <div class="mt-3 p-3 rounded-[var(--skz-radius-low)] bg-[#f5f8ff] border border-[var(--skz-border-color-main)] overflow-x-auto">
            <markdown [data]="'$$' + previewLatex + '$$'" katex></markdown>
          </div>
        }
      </mat-dialog-content>

      <mat-dialog-actions class="footer">
        <div class="right-actions ml-auto">
          <button mat-button (click)="dialogRef.close()">{{ 'Global.Cancel' | translate }}</button>
          <button mat-flat-button color="primary" (click)="submit()" [disabled]="!expression.trim()">{{ 'Global.Insert' | translate }}</button>
        </div>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MathEquationDialogComponent {
  dialogRef = inject(MatDialogRef<MathEquationDialogComponent>);

  expression = '';

  get previewLatex(): string {
    if (!this.expression.trim()) return '';
    try {
      return parse(this.expression).toTex();
    } catch {
      return '';
    }
  }

  submit() {
    if (!this.expression.trim()) return;
    try {
      const latex = parse(this.expression).toTex();
      this.dialogRef.close(latex);
    } catch (error) {
      // If parsing fails, we could show an error, but fallback to raw string
      this.dialogRef.close(this.expression);
    }
  }
}
